import React, { useEffect, useRef, useState } from "react";
import { Button, Image, Input, Modal } from "antd";
import {
  CloseCircleOutlined,
  EditOutlined,
  FileOutlined,
  PaperClipOutlined,
  SendOutlined,
} from "@ant-design/icons";

function FilePreview({ entry, size }) {
  if (entry.previewUrl && entry.file.type.startsWith("image/")) {
    return <Image src={entry.previewUrl} alt={entry.file.name} width={size} height={size} preview={false} style={{ objectFit: "cover", borderRadius: 10 }} />;
  }
  return (
    <div className="internal-chat-review-document" style={{ width: size, height: size }}>
      <FileOutlined />
      <span>{entry.file.name.split(".").pop()?.toUpperCase() || "FILE"}</span>
    </div>
  );
}

export default function InternalAttachmentReviewModal({
  open,
  files,
  recipientName,
  activeId,
  priorityLabel,
  onSetActive,
  onUpdateCaption,
  onRemoveFile,
  onDiscardAll,
  onSendAll,
  onAddFiles,
  onEditFile,
  sending,
}) {
  const [dragging, setDragging] = useState(false);
  const pickerRef = useRef(null);
  const activeIndex = Math.max(0, files.findIndex(item => item.id === activeId));
  const active = files[activeIndex] || files[0] || null;

  useEffect(() => {
    if (files.length && !files.some(item => item.id === activeId)) onSetActive(files[0].id);
  }, [activeId, files, onSetActive]);

  if (!active) return null;
  return (
    <Modal
      open={open}
      onCancel={onDiscardAll}
      title={`Send to ${recipientName || "colleague"}`}
      width={640}
      zIndex={1400}
      destroyOnHidden
      footer={[
        <Button key="cancel" onClick={onDiscardAll}>Cancel</Button>,
        <Button key="send" type="primary" icon={<SendOutlined />} loading={sending} onClick={onSendAll}>
          Send{files.length > 1 ? ` All (${files.length})` : ""}
        </Button>,
      ]}
    >
      <div
        className="internal-chat-review"
        onDragOver={event => { event.preventDefault(); setDragging(true); }}
        onDragLeave={event => { if (!event.currentTarget.contains(event.relatedTarget)) setDragging(false); }}
        onDrop={event => { event.preventDefault(); setDragging(false); onAddFiles(event.dataTransfer.files); }}
      >
        {dragging && <div className="internal-chat-review-drop"><PaperClipOutlined /><strong>Drop to add more files</strong></div>}
        <input ref={pickerRef} type="file" multiple accept="image/jpeg,image/png,image/gif,image/webp,image/heic,image/heif,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.vcf" hidden onChange={event => { onAddFiles(event.target.files); event.target.value = ""; }} />

        <div className="internal-chat-review-preview">
          <FilePreview entry={active} size={230} />
          {active.file.type.startsWith("image/") && (
            <Button size="small" icon={<EditOutlined />} onClick={() => onEditFile(active.id)} className="internal-chat-review-edit">Edit</Button>
          )}
        </div>
        <div className="internal-chat-review-name">
          <strong>{active.file.name}</strong>
          <span>{Math.ceil(active.file.size / 1024)} KB · {priorityLabel} priority</span>
        </div>
        <Input.TextArea rows={2} placeholder="Add a caption…" value={active.caption} onChange={event => onUpdateCaption(active.id, event.target.value)} />

        <div className="internal-chat-review-footer-row">
          <div className="internal-chat-review-thumbnails">
            {files.map(entry => (
              <div role="button" tabIndex={0} key={entry.id} className={`internal-chat-review-thumb ${entry.id === active.id ? "is-active" : ""}`} onClick={() => onSetActive(entry.id)} onKeyDown={event => { if (event.key === "Enter") onSetActive(entry.id); }}>
                <FilePreview entry={entry} size={58} />
                <span className="internal-chat-review-remove" role="button" tabIndex={0} onClick={event => { event.stopPropagation(); onRemoveFile(entry.id); }} onKeyDown={event => { if (event.key === "Enter") { event.stopPropagation(); onRemoveFile(entry.id); } }}><CloseCircleOutlined /></span>
              </div>
            ))}
          </div>
          <Button icon={<PaperClipOutlined />} onClick={() => pickerRef.current?.click()}>Add files</Button>
        </div>
      </div>
    </Modal>
  );
}
